/**
 * PortalMap.jsx — customer-portal map with the job-site pin + PII-free
 * crew-arrival pin drops. OpenStreetMap tiles (no API key).
 */
import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const siteIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const arrivalIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const enRouteIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0], 15);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
    }
  }, [points, map]);
  return null;
}

export const PortalMap = ({ siteLocation, arrivalPins = [], enRoutePin = null }) => {
  const points = [];
  if (siteLocation?.lat != null && siteLocation?.lon != null) {
    points.push([siteLocation.lat, siteLocation.lon]);
  }
  arrivalPins.forEach((p) => {
    if (p.lat != null && p.lon != null) points.push([p.lat, p.lon]);
  });
  if (enRoutePin?.lat != null && enRoutePin?.lon != null) {
    points.push([enRoutePin.lat, enRoutePin.lon]);
  }
  if (points.length === 0) return null;

  return (
    <div className="h-64 rounded-md overflow-hidden border" data-testid="portal-map">
      <MapContainer center={points[0]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {siteLocation?.lat != null && (
          <Marker position={[siteLocation.lat, siteLocation.lon]} icon={siteIcon}>
            <Popup>Job site</Popup>
          </Marker>
        )}
        {arrivalPins.map((p, i) => (
          p.lat != null && p.lon != null ? (
            <Marker key={`arr-${i}`} position={[p.lat, p.lon]} icon={arrivalIcon}>
              <Popup>Crew arrived{p.at ? ` — ${fmt(p.at)}` : ''}</Popup>
            </Marker>
          ) : null
        ))}
        {enRoutePin?.lat != null && enRoutePin?.lon != null && (
          <Marker position={[enRoutePin.lat, enRoutePin.lon]} icon={enRouteIcon}>
            <Popup>Crew is on the way{enRoutePin.since ? ` — since ${fmt(enRoutePin.since)}` : ''}</Popup>
          </Marker>
        )}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
};
